"use client";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCopyFeedback } from "../../hooks";
import DemoPanel, {
  demoGridClass,
  demoHeadingClass,
} from "../demoPanel/demoPanel.component";
import { useRepeatPlan } from "./hooks";

interface RepeatAction {
  key: string;
  label: string;
  // One task per week (four).
  weeks: string[];
}

export interface RepeatPlanCopy {
  heading: string;
  actions: RepeatAction[];
  maxActions: number;
  limitNote: string;
  copyLabel: string;
  copiedLabel: string;
  // `{actions}` is the ticked actions' names.
  copyTitle: string;
  resultHeading: string;
  empty: string;
  // The four weeks, with an optional fixed reminder listed first.
  weeks: { label: string; lead?: string }[];
}

interface RepeatPlanBuilderProps {
  copy: RepeatPlanCopy;
}

// The preview's `repeat_plan_builder`: tick up to three actions and get a
// week by week plan you can copy.
const RepeatPlanBuilder = ({ copy }: RepeatPlanBuilderProps) => {
  const plan = useRepeatPlan(copy);
  const { isCopied, copy: copyText } = useCopyFeedback();
  return (
    <div className={demoGridClass}>
      <DemoPanel>
        <fieldset className="grid gap-1.5">
          <legend className={cn(demoHeadingClass, "mb-2")}>{copy.heading}</legend>
          {copy.actions.map((action) => {
            const isOn = plan.selected.includes(action.key);
            const isDisabled = !isOn && plan.isFull;
            return (
              <label
                key={action.key}
                className={cn(
                  "flex items-center gap-2.5 rounded-[14px] border-[1.5px] px-3 py-2.5 text-[15px] font-semibold transition-colors motion-reduce:transition-none",
                  isOn ? "border-primary bg-tone-blush" : "border-line bg-white",
                  isDisabled ? "cursor-not-allowed text-ink-muted" : "cursor-pointer",
                )}
              >
                <input
                  type="checkbox"
                  checked={isOn}
                  disabled={isDisabled}
                  onChange={() => plan.toggle(action.key)}
                  className="size-[18px] shrink-0 accent-primary"
                />
                <span>{action.label}</span>
              </label>
            );
          })}
        </fieldset>
        <p aria-live="polite" className="min-h-5 text-sm text-ink-muted">
          {plan.isFull ? copy.limitNote : ""}
        </p>
        <Button
          type="button"
          variant="brandOutline"
          size="pillSm"
          disabled={!plan.hasPlan}
          onClick={() => copyText(plan.planText)}
          className="justify-self-start"
        >
          {isCopied ? copy.copiedLabel : copy.copyLabel}
        </Button>
      </DemoPanel>
      <DemoPanel>
        <h3 className={demoHeadingClass}>{copy.resultHeading}</h3>
        <div aria-live="polite" className="grid gap-3">
          {plan.hasPlan ? (
            plan.weeks.map((week) => (
              <div key={week.label} className="border-l-4 border-primary py-1 pl-3.5">
                <b className="mb-1 block text-base">{week.label}</b>
                <ul className="list-disc space-y-1 pl-[18px] text-[15px] leading-normal">
                  {week.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))
          ) : (
            <p className="text-base text-ink-muted">{copy.empty}</p>
          )}
        </div>
      </DemoPanel>
    </div>
  );
};

export default RepeatPlanBuilder;
